import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { LinkIcon, LockIcon } from 'lucide-react';
import {
  AuthShell,
  BrandMark,
  Button,
  Callout,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  PasswordInput,
  ResultState,
  toast,
} from '@opengoal/ui';
import { ApiError, normalizeAuthError } from '@opengoal/http';
import { useTranslation } from 'react-i18next';
import { env } from '@/app/env';
import { authClient } from '@/lib/auth-client';

export function ResetPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = useSearch({ from: '/_auth/reset-password' });

  const schema = z
    .object({
      password: z.string().min(1, t('auth.passwordRequired')).min(8, t('auth.passwordTooShort')),
      confirmPassword: z.string().min(1, t('auth.confirmPasswordRequired')),
    })
    .refine((values) => values.password === values.confirmPassword, {
      path: ['confirmPassword'],
      message: t('auth.passwordMismatch'),
    });
  type ResetPasswordFormValues = z.infer<typeof schema>;

  const form = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { password: '', confirmPassword: '' },
  });

  const resetPassword = useMutation({
    mutationFn: async (values: ResetPasswordFormValues) => {
      // Guarded by the `token` check below, so this branch is unreachable from
      // the form — it exists so the non-null assertion never has to be written.
      if (!search.token) throw new ApiError({ status: 400, message: t('auth.errors.generic') });

      const { error } = await authClient.resetPassword({
        newPassword: values.password,
        token: search.token,
      });
      if (error) throw normalizeAuthError(error);
    },
    onSuccess: () => {
      // Resetting revokes every other session server-side, so there is nothing
      // to preserve — send them to sign in with the new password.
      toast.success(t('auth.passwordUpdatedTitle'));
      void navigate({ to: '/login' });
    },
  });

  const brand = <BrandMark name={env.appName} tagline={t('app.tagline')} />;

  // An expired or already-used link arrives with no token at all, so this is
  // worth catching before showing a form that could only ever fail.
  if (!search.token) {
    return (
      <AuthShell brand={brand} title={t('auth.resetTokenMissingTitle')}>
        <ResultState
          tone="warning"
          icon={<LinkIcon />}
          description={t('auth.resetTokenMissingBody')}
          className="px-0 py-2"
          action={
            <Button asChild>
              <Link to="/forgot-password">{t('auth.requestNewLink')}</Link>
            </Button>
          }
        />
      </AuthShell>
    );
  }

  return (
    <AuthShell
      brand={brand}
      title={t('auth.resetTitle')}
      footer={
        <Link to="/login" className="text-muted-foreground hover:text-foreground">
          {t('auth.backToSignIn')}
        </Link>
      }
    >
      <Form {...form}>
        <form
          noValidate
          className="grid gap-5"
          onSubmit={form.handleSubmit((values) => resetPassword.mutate(values))}
        >
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('auth.newPassword')}</FormLabel>
                <FormControl>
                  <PasswordInput
                    icon={<LockIcon />}
                    autoComplete="new-password"
                    className="h-10"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="confirmPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('auth.confirmPassword')}</FormLabel>
                <FormControl>
                  <PasswordInput
                    icon={<LockIcon />}
                    autoComplete="new-password"
                    className="h-10"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {resetPassword.isError ? (
            <Callout variant="destructive" title={t('auth.resetTokenMissingBody')} />
          ) : null}

          <Button type="submit" size="lg" className="w-full" loading={resetPassword.isPending}>
            {t('auth.updatePassword')}
          </Button>
        </form>
      </Form>
    </AuthShell>
  );
}
